import { capitalize, hexToRgb } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import { ColumnFields, MarketsGroupedByMarketType, SelectOption } from '../@types';
import { BetStatus, BettingSlipAcceptStatus, BettingSlipStatus, Market, WithdrawalStatus } from '../@types/api';
import { AdditionalDataGridColumnProps } from '../components/molecules/CustomizeTable';
import { COMMON_COLUMN_PROPS } from '../constants/table';
import { WITHDRAWAL_STATUS } from '../constants/enums';

export const formatSnakeCaseString = (str: string, capitalizeEachWord = true) => {
  const words = str.toLowerCase().split(/_/g);

  const capitalizedWords = [
    capitalize(words[0]),
    ...words.slice(1).map((word) => (capitalizeEachWord ? capitalize(word) : word)),
  ];

  return capitalizedWords.join(' ');
};

export const hexToRgba = (hex: string, alpha: number) => {
  const rgb = hexToRgb(hex);

  return rgb.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
};

export const formatBreadcrumb = (pathSegment: string) => {
  if (pathSegment.length <= 3) {
    return pathSegment.toUpperCase();
  }

  return pathSegment.replace(/-/g, ' ');
};

export const createColumn = (
  field: ColumnFields | string,
  headerName: string,
  specificProps?: AdditionalDataGridColumnProps & Omit<GridColDef, 'field' | 'headerName'>
) => ({
  field,
  headerName,
  ...COMMON_COLUMN_PROPS,
  ...specificProps,
});

export const getBool = (value?: number | boolean): boolean | undefined => {
  // Return the boolean representation for both numbers and booleans.
  // If value is undefined, !!undefined returns false, but we want to maintain undefined.
  return value === undefined ? undefined : !!value;
};

export const prepareBoolValue = (value: number | boolean | undefined | null): boolean | undefined => {
  return value !== undefined && value !== null ? !!value : undefined;
};
export const getBettingSlipAcceptStatusColor = (status: BettingSlipAcceptStatus) => {
  switch (status) {
    case 'accepted':
      return '#78B159';
    case 'pending':
      return '#F7B64E';
    case 'rejected':
      return '#DC362E';
    case 'admin_cancelled':
      return '#938F99';
    default:
      return '#000000';
  }
};

export const getApprovalStatusColor = (status: WithdrawalStatus) => {
  switch (status) {
    case WITHDRAWAL_STATUS.REQUESTED:
      return 'info.main';
    case WITHDRAWAL_STATUS.APPROVED:
      return 'success.main';
    case WITHDRAWAL_STATUS.PENDING:
      return 'warning.main';
    case WITHDRAWAL_STATUS.REJECTED:
      return 'error.main';
    default:
      return 'text.primary';
  }
};

export const getBettingSlipStatusColor = (status: BettingSlipStatus) => {
  switch (status) {
    case 'open':
      return '#2B7FFE';
    case 'won':
      return '#14805C';
    case 'lost':
      return '#B3261E';
    case 'void':
      return '#9F8FEF';
    case 'cashout':
      return '#F7B61E';
    default:
      return '#000000';
  }
};

export const getBetStatusColor = (status: BetStatus) => {
  switch (status) {
    case 'won':
      return '#14805C';
    case 'lost':
      return '#B3261E';
    case 'void':
      return '#9F8FEF';
    case 'open':
      return '#2B7FFE';
    case 'half_won':
      return '#FFC107';
    case 'half_lost':
      return '#FFC107';
    default:
      return '#000000';
  }
};

const isMultipleDisplayType = (market: Market) => {
  return !market.isSingleDisplayType && market.specialValues.length > 0;
};

export const formatMarketsData = (markets?: Market[]) => {
  const result: (Market | MarketsGroupedByMarketType)[] = [];

  markets?.forEach((market) => {
    if (isMultipleDisplayType(market)) {
      const existingItem = result.find((item) => item.id === market.marketTypeId);

      if (existingItem && 'markets' in existingItem) {
        existingItem.markets.push(market);
      } else {
        const marketName = market.marketType?.shortName || market.marketType?.name || '';
        result.push({
          id: market.marketTypeId,
          marketType: market.marketTypeId,
          name: `${marketName}, ${market.marketType?.eventPartName}`,
          markets: [market],
          isCollapsedMode: market.isCollapsedMode,
          position: market.position,
          specialValues: market.specialValues,
          outcomes: market.outcomes,
        });
      }
    } else {
      result.push(market);
    }
  });

  return result;
};

export const formatNumberToStandard = (value: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

export const floorToFixed = (value: number, decimals: number) => {
  const factor = 10 ** decimals;

  return (Math.floor(value * factor) / factor).toFixed(decimals);
};

export const ensureArray = <T>(value: T | T[]) => {
  return Array.isArray(value) ? value : [value];
};

export const generateZodEnumIds = <T extends SelectOption>(options: T[] | readonly T[]) => {
  return options.map((option) => option.id) as [T['id'], ...T['id'][]];
};

export const toSelectOption = <T extends string>(value: T) => ({ id: value, name: value });

export const getFullName = (firstName?: string | null, lastName?: string | null, username?: string) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return username ?? '';
};

export const getCompleteName = (firstName?: string | null, lastName?: string | null, username?: string) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName} (${username})`;
  }
  return username ?? '';
};

export const copyToClipboard = (text: string): Promise<void> => {
  if (navigator?.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }

  // Fallback
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;

    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';

    textarea.setAttribute('readonly', '');

    document.body.appendChild(textarea);

    try {
      textarea.select();
      textarea.setSelectionRange(0, 99999);

      const success = document.execCommand('copy');
      if (success) {
        resolve();
      } else {
        reject(new Error('Failed to copy text to clipboard.'));
      }
    } catch (err) {
      reject(err);
    } finally {
      document.body.removeChild(textarea);
    }
  });
};

export { normalizePaymentMethodFilter } from './form';
